import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

// Estados do contexto
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isLoading: true,
  isAuthenticated: false,
};

// Tipos de ações
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  VERIFY_TOKEN_START: 'VERIFY_TOKEN_START',
  VERIFY_TOKEN_SUCCESS: 'VERIFY_TOKEN_SUCCESS',
  VERIFY_TOKEN_FAILURE: 'VERIFY_TOKEN_FAILURE',
};

// Reducer para gerenciar o estado
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.VERIFY_TOKEN_START:
      return {
        ...state,
        isLoading: true,
      };

    case AUTH_ACTIONS.VERIFY_TOKEN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
      };

    case AUTH_ACTIONS.VERIFY_TOKEN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };

    default:
      return state;
  }
};

// Criar contexto
const AuthContext = createContext();

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Provider do contexto
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const getPermissionCodeFromUser = (user) => {
    if (!user) return null;

    if (typeof user.permissao === 'string') {
      return user.permissao.toUpperCase();
    }

    if (user.permissao?.codigo) {
      return String(user.permissao.codigo).toUpperCase();
    }

    if (user.permissao_codigo) {
      return String(user.permissao_codigo).toUpperCase();
    }

    if (user.permission || user.permissionCode) {
      return String(user.permission || user.permissionCode).toUpperCase();
    }

    return null;
  };

  const normalizeUser = (user) => {
    if (!user) return null;

    const permissaoCodigo = getPermissionCodeFromUser(user);

    return {
      ...user,
      permissao_codigo: permissaoCodigo ?? user.permissao_codigo,
      permissao: user.permissao ?? permissaoCodigo ?? null,
      permissaoCodigo,
    };
  };

  // Verificar expiração do token JWT
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      // Decodificar o token JWT (sem verificar assinatura)
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Verificar se tem expiração
      if (!payload.exp) return false;
      
      // Converter timestamp para milissegundos e verificar se expirou
      const expirationTime = payload.exp * 1000;
      return Date.now() >= expirationTime;
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return true; // Em caso de erro, considerar expirado
    }
  };

  // Verificar token ao inicializar
  useEffect(() => {
    const verifyToken = async () => {
      if (!state.token) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }

      // Verificar se token expirou localmente
      if (isTokenExpired(state.token)) {
        console.log('Token expirado, redirecionando para login');
        localStorage.removeItem('token');
        dispatch({ type: AUTH_ACTIONS.VERIFY_TOKEN_FAILURE });
        return;
      }

      try {
        dispatch({ type: AUTH_ACTIONS.VERIFY_TOKEN_START });
        
        // Decodificar token antes de verificar
        let tokenPayload = null;
        try {
          tokenPayload = JSON.parse(atob(state.token.split('.')[1]));
        } catch (e) {
          console.error('[AuthContext] Erro ao decodificar token:', e);
        }
        
        const response = await authAPI.verifyToken(state.token);
        
        // Validação crítica
        if (tokenPayload && tokenPayload.userId !== response.data.user?.id) {
          console.error('[AuthContext] 🚨 ERRO CRÍTICO: userId do token não corresponde ao userId do usuário retornado!');
          console.error('[AuthContext] Token userId:', tokenPayload.userId);
          console.error('[AuthContext] User id retornado:', response.data.user?.id);
        }
        
        const normalizedUser = normalizeUser(response.data.user);

        dispatch({
          type: AUTH_ACTIONS.VERIFY_TOKEN_SUCCESS,
          payload: { user: normalizedUser }
        });
      } catch (error) {
        console.error('Erro ao verificar token:', error);
        localStorage.removeItem('token');
        dispatch({ type: AUTH_ACTIONS.VERIFY_TOKEN_FAILURE });
      }
    };

    verifyToken();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Verificar expiração do token periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem('token');
      if (currentToken && isTokenExpired(currentToken)) {
        console.log('Token expirou durante a sessão, redirecionando para login');
        localStorage.removeItem('token');
        dispatch({ type: AUTH_ACTIONS.VERIFY_TOKEN_FAILURE });
        window.location.href = '/';
      }
    }, 30000); // Verificar a cada 30 segundos

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Função de login
  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      
      // Tentar criptografar a senha com RSA
      let senhaEncrypted = null;
      try {
        const { encryptPassword } = await import('../utils/rsaEncryption');
        senhaEncrypted = await encryptPassword(password);
      } catch (encryptError) {
        console.warn('Erro ao criptografar senha, enviando em texto (compatibilidade):', encryptError);
        // Continuar com senha em texto para compatibilidade
      }
      
      const response = await authAPI.login(email, password, senhaEncrypted);
      
      if (import.meta.env.DEV) {
        console.debug('[AuthContext] === RESPOSTA DO BACKEND (LOGIN) ===');
        console.debug('[AuthContext] Email usado no login:', email);
        console.debug('[AuthContext] Token recebido:', response.data.token?.substring(0, 20) + '...');
        console.debug('[AuthContext] Usuário recebido:', { 
          id: response.data.user?.id,
          nome: response.data.user?.nome,
          email: response.data.user?.email,
          tipo: response.data.user?.tipo,
          permissao_codigo: response.data.user?.permissao_codigo,
          permissao: response.data.user?.permissao
        });
        console.debug('[AuthContext] Objeto user completo:', response.data.user);
      }
      
      // Verificar se precisa resetar senha
      if (response.data.requiresPasswordReset) {
        dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE });
        return { 
          success: false, 
          requiresPasswordReset: true,
          user: response.data.user
        };
      }
      
      const { token, user } = response.data;

      // VALIDAÇÃO CRÍTICA: Verificar se o token corresponde ao usuário
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        if (tokenPayload.userId !== user.id) {
          console.error('[AuthContext] 🚨 ERRO CRÍTICO: userId do token não corresponde ao userId do usuário!');
          console.error('[AuthContext] Token userId:', tokenPayload.userId);
          console.error('[AuthContext] User id:', user.id);
          throw new Error('Inconsistência na autenticação');
        }
        
        if (import.meta.env.DEV) {
          console.debug('[AuthContext] ✅ Validação: Token userId corresponde ao user id');
        }
      } catch (error) {
        console.error('[AuthContext] Erro ao validar token:', error);
        throw error;
      }

      // Salvar token no localStorage
      const oldToken = localStorage.getItem('token');
      if (oldToken && import.meta.env.DEV) {
        console.debug('[AuthContext] Token anterior encontrado no localStorage, substituindo...');
      }
      localStorage.setItem('token', token);

      const normalized = normalizeUser(user);
      
      if (import.meta.env.DEV) {
        console.debug('[AuthContext] Usuário normalizado:', normalized);
      }

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { token, user: normalized }
      });

      if (import.meta.env.DEV) {
        console.debug('[AuthContext] Login bem-sucedido', { user: normalized });
      }

      toast.success(`Bem-vindo, ${user.nome}!`);
      return { success: true, user };
    } catch (error) {
      console.error('Erro no login:', error);
      
      const errorMessage = error.response?.data?.error || 'Erro ao fazer login';
      toast.error(errorMessage);
      
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE });
      return { success: false, error: errorMessage };
    }
  };

  // Função de logout
  const logout = async () => {
    try {
      if (state.token) {
        await authAPI.logout(state.token);
      }
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      // Sempre limpar o estado local, mesmo se a requisição falhar
      localStorage.removeItem('token');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.success('Logout realizado com sucesso');
    }
  };

  // Verificar se é administrador
  const isAdmin = () => {
    return state.user?.tipo === 'admin';
  };

  const getPermissionCode = () => getPermissionCodeFromUser(state.user);

  const hasPermission = (codigo) => {
    if (!codigo) {
      return false;
    }

    const currentCode = getPermissionCode();

    if (!currentCode) {
      return false;
    }

    return currentCode === String(codigo).toUpperCase();
  };

  const isGestor = () => hasPermission('GESTOR');

  // Verificar se é usuário comum
  const isUser = () => {
    return state.user?.tipo === 'user';
  };

  // Obter token para requisições
  const getToken = () => {
    return state.token;
  };

  // Valor do contexto
  const value = {
    ...state,
    login,
    logout,
    isAdmin,
    isGestor,
    isUser,
    getToken,
    hasPermission,
    getPermissionCode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
